if (!customElements.get('v6-pdp-gallery')) {
  customElements.define('v6-pdp-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.items = Array.from(this.querySelectorAll('[data-gallery-index]'));
      this.panels = Array.from(this.querySelectorAll('[data-gallery-panel]'));
      this.dialog = this.querySelector('dialog');
      this.zoomStage = this.querySelector('[data-gallery-zoom-stage]');
      if (!this.items.length || this.items.length !== this.panels.length || !this.dialog) return;
      this.currentIndex = Math.max(0, this.items.findIndex(item => item.getAttribute('aria-current') === 'true'));
      this.items.forEach(item => item.setAttribute('role', 'button'));
      this.querySelector('[data-gallery-controls]').hidden = this.items.length < 2;
      this.addEventListener('click', event => {
        const thumbnail = event.target.closest('[data-gallery-index]');
        const step = event.target.closest('[data-gallery-step]');
        const opener = event.target.closest('[data-gallery-open]');
        if (thumbnail) {
          event.preventDefault();
          this.select(Number(thumbnail.dataset.galleryIndex));
        } else if (step) {
          this.select(this.currentIndex + Number(step.dataset.galleryStep));
        } else if (event.target.closest('[data-gallery-close]') || event.target === this.dialog) {
          this.dialog.close();
        } else if (opener && typeof this.dialog.showModal === 'function') {
          event.preventDefault();
          if (performance.now() < (this.suppressClickUntil || 0) || this.dialog.open) return;
          this.lastOpener = opener;
          this.updateZoom();
          this.dialog.showModal();
          this.querySelector('[data-gallery-close]').focus();
        }
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.dialog.open) {
          event.preventDefault();
          event.stopPropagation();
          this.dialog.close();
          return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input,textarea,select,video,iframe,model-viewer')) return;
        if (event.key === ' ' && event.target.closest('[data-gallery-index]')) {
          event.preventDefault();
          event.target.closest('[data-gallery-index]').click();
          return;
        }
        let next;
        if (event.key === 'ArrowRight') next = this.currentIndex + 1;
        if (event.key === 'ArrowLeft') next = this.currentIndex - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.items.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.select(next);
        if (event.target.closest('[data-gallery-index]')) this.items[this.currentIndex].focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('close', () => {
        this.pauseMedia(this.zoomStage);
        this.zoomStage.replaceChildren();
        if (this.isConnected) {
          const opener = this.panels[this.currentIndex].querySelector('[data-gallery-open]') || this.items[this.currentIndex] || this.lastOpener;
          opener?.focus({ preventScroll: true });
        }
      }, { signal });
      [this.querySelector('[data-gallery-stage]'), this.zoomStage].forEach(surface => {
        surface.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.pointerType === 'mouse' || event.target.closest('button,video,iframe,model-viewer')) return;
          this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        }, { signal, passive: true });
        surface.addEventListener('pointerup', event => {
          const gesture = this.gesture;
          this.gesture = null;
          if (!gesture || gesture.id !== event.pointerId) return;
          const dx = event.clientX - gesture.x;
          const dy = event.clientY - gesture.y;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
          this.suppressClickUntil = performance.now() + 450;
          this.select(this.currentIndex + (dx < 0 ? 1 : -1));
        }, { signal, passive: true });
        surface.addEventListener('pointercancel', () => { this.gesture = null; }, { signal, passive: true });
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.pauseMedia(this);
      this.gesture = null;
    }

    pauseMedia(surface) {
      surface?.querySelectorAll('video').forEach(video => video.pause());
      surface?.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      surface?.querySelectorAll('iframe').forEach(frame => {
        // Reload only an already mounted external player to stop playback reliably.
        if (frame.getAttribute('src')) frame.src = frame.src;
      });
    }

    mountPanel(index) {
      const panel = this.panels[index];
      const template = panel.querySelector('template[data-gallery-template]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      return panel;
    }

    select(index) {
      if (!Number.isFinite(index) || !this.items.length) return;
      const nextIndex = ((index % this.items.length) + this.items.length) % this.items.length;
      if (nextIndex !== this.currentIndex) this.pauseMedia(this.panels[this.currentIndex]);
      this.currentIndex = nextIndex;
      this.mountPanel(nextIndex);
      this.panels.forEach((panel, i) => { panel.hidden = i !== nextIndex; });
      this.items.forEach((thumbnail, i) => {
        if (i === nextIndex) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      this.querySelectorAll('[data-gallery-count]').forEach(count => {
        count.textContent = `${nextIndex + 1} / ${this.items.length}`;
      });
      const status = this.dataset.announcement.replace('[index]', String(nextIndex + 1));
      this.querySelector('[data-gallery-status]').textContent = status;
      this.querySelector('[data-gallery-zoom-status]').textContent = status;
      const item = this.items[nextIndex];
      const strip = item.closest('ul');
      const itemRect = item.getBoundingClientRect();
      const stripRect = strip.getBoundingClientRect();
      if (itemRect.left < stripRect.left || itemRect.right > stripRect.right) {
        strip.scrollTo({ left: item.offsetLeft - strip.offsetLeft - (strip.clientWidth - item.clientWidth) / 2, behavior: 'auto' });
      }
      if (this.dialog.open) this.updateZoom();
    }

    // Preserve compatibility with native Shopify gallery media associations.
    setActiveMedia(mediaId) {
      const id = String(mediaId).split('-').pop();
      const index = this.panels.findIndex(panel => {
        const content = panel.querySelector('template')?.content || panel;
        return Array.from(content.querySelectorAll('[data-media-id]')).some(media => media.dataset.mediaId === id);
      });
      if (index >= 0) this.select(index);
    }

    updateZoom() {
      this.pauseMedia(this.zoomStage);
      const source = this.mountPanel(this.currentIndex).querySelector('[data-gallery-slide]');
      const clone = source.cloneNode(true);
      clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      clone.querySelectorAll('[data-gallery-open]').forEach(link => link.replaceWith(...link.childNodes));
      clone.querySelectorAll('.v6-pdp-gallery__zoom').forEach(icon => icon.remove());
      clone.querySelectorAll('[data-gallery-image]').forEach(image => {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.loading = 'eager';
        image.fetchPriority = 'auto';
        image.src = image.dataset.large;
      });
      this.pauseMedia(this.panels[this.currentIndex]);
      this.zoomStage.replaceChildren(clone);
    }
  });
}
